/* RESPONSIBLE TEAM: team-inbox */
import { helper } from '@ember/component/helper';
// @ts-ignore
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';
import { htmlSafe } from '@ember/template';

/**
 * A specialized sanitization helper for macro names that:
 * 1. Uses the standard sanitize-html with default config as the first pass
 * 2. Adds additional protection against the specific SVG/style CSS injection attack
 * 3. Preserves search highlighting with <mark> tags
 * 4. Allows basic HTML formatting while blocking dangerous elements
 */
const sanitizeMacroNameHelper = helper(function sanitizeMacroName([unsafe]: [string]) {
  if (!unsafe || typeof unsafe !== 'string') {
    return '';
  }

  // First pass: use the standard sanitization with default config
  // This handles most security concerns and allows basic HTML formatting
  let firstPassResult = sanitizeHtml(unsafe, { config: 'default' });

  // Convert back to string for additional processing
  let htmlString = firstPassResult.toString();

  // Second pass: specifically block the SVG/style attack vector
  // Remove any remaining <svg> and <style> tags that might have slipped through
  htmlString = htmlString
    .replace(/<svg[^>]*>[\s\S]*?<\/svg>/gi, '') // Remove SVG tags and content (including newlines)
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '') // Remove style tags and content (including newlines)
    .replace(/<svg[^>]*>/gi, '') // Remove unclosed SVG tags
    .replace(/<\/svg>/gi, '') // Remove closing SVG tags
    .replace(/<style[^>]*>/gi, '') // Remove unclosed style tags
    .replace(/<\/style>/gi, '') // Remove closing style tags
    .replace(/\sstyle\s*=\s*["'][^"']*["']/gi, '') // Remove style attributes
    .replace(/\sstyle\s*=\s*[^>\s]+/gi, ''); // Remove unquoted style attributes

  // This is safe to use htmlSafe because:
  // 1. We've already sanitized with the standard sanitizer
  // 2. We've specifically removed the dangerous SVG/style elements
  // 3. The result should only contain safe HTML elements allowed by the default config
  // eslint-disable-next-line @intercom/intercom/no-html-safe
  return htmlSafe(htmlString);
});

export default sanitizeMacroNameHelper;

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'sanitize-macro-name': typeof sanitizeMacroNameHelper;
  }
}
